/* eslint-disable @typescript-eslint/no-explicit-any */
import { CIRCLE_STEPS, HEATMAP_BY_TYPES } from './constants'
import { circle } from '@turf/turf'
import { getAppMetadata } from './metadata/metadata'
import { getAudienceFeatureVersion } from './metadata/metadata.helper'
import { Audience, AudiencesQuintile, Plan } from '@workspaces/types'

function deserializeCountries(countries: any[]) {
  // validate no null values in the country array
  const checkedCountries = countries.filter((c) => c)
  if (!checkedCountries.length) {
    return getAppMetadata().countries.map((c) => c.id)
  }
  return checkedCountries
}

function deserializeGeoboundaries(geoboundaries: any) {
  return geoboundaries
}

function deserializeUserAudiencesGroup(userAudiencesGroup: any[]) {
  // Legacy purposes, id needs to be set if legacy plan have it unset
  const userAudiencesGroupWithId = userAudiencesGroup.map((audience) => {
    if (!audience.id) {
      const time = new Date().getTime()
      audience.id = `audience-${time}`
    }
    return audience
  })
  return userAudiencesGroupWithId
}

function deserializeAudiences(audiences: any) {
  const audiencesDeserialized = {
    audiences: audiences.audiences || [],
    audiencesGroups: audiences.audiencesGroups || [],
    audiencesIndex: audiences.audiencesIndex,
    audiencesPercent: audiences.audiencesPercent,
    audiencesType: audiences.audiencesType,
    delivery: audiences.delivery || null,
    userAudiencesGroup: audiences.userAudiencesGroup
      ? deserializeUserAudiencesGroup(audiences.userAudiencesGroup)
      : [],
  }
  // We may have old plans saving delivery for V1, so this is a hack to always use Latest Delivery for V1
  if (getAudienceFeatureVersion() === Audience.AudienceFeatureVersion.V1) {
    audiencesDeserialized.delivery = null
  }
  return audiencesDeserialized
}

function deserializePolygons(features: any) {
  const parsedFeatures =
    typeof features === 'string' ? JSON.parse(features) : features
  if (
    features.type === 'FeatureCollection' &&
    Array.isArray(features.features)
  ) {
    return features
  }
  return {
    type: 'FeatureCollection',
    features: parsedFeatures.map((f: any) => {
      if (f.geometry.type === 'Point') {
        if (f.properties.units === 'meters') {
          // TODO: convert to kilometers
        }
        const center = [f.properties.center.lng, f.properties.center.lat]
        const circleFeature = circle(center, f.properties.radius, {
          ...f.properties,
          steps: CIRCLE_STEPS,
        })
        return { ...circleFeature, properties: { ...f.properties }, id: f.id }
      }
      return f
    }),
  }
}

function deserializeGeographicalDistribution(geographicalDistribution: any) {
  return geographicalDistribution
}

function deserializeDigitalPanel(isDigital: any) {
  return isDigital === '' ? undefined : isDigital
}

function removeUndefinedProperty<T>(key: string, obj: T): T {
  const keyTyped: keyof T = key as unknown as keyof T
  if (obj[keyTyped] === undefined) {
    delete obj[keyTyped]
  }
  return obj
}

function deserializeAudiencesQuintile(
  audiencesQuintile: string | undefined | null,
): AudiencesQuintile.PlanFilterParam | null {
  if (!audiencesQuintile || typeof audiencesQuintile !== 'string') {
    return {
      quintiles: [1],
      audiences: [],
    }
  }
  const valueInJson = JSON.parse(audiencesQuintile)
  return valueInJson
}

export function deserializeFilters(filters: any) {
  const objFilters = JSON.parse(filters)

  return objFilters.map((subfilter: any) => {
    const deserialized = {
      tabname: subfilter.tabname || '',
      countries: deserializeCountries(subfilter.countries),
      geoboundaries: deserializeGeoboundaries(subfilter.geoboundaries),
      types: subfilter.types,
      assets_excluded: subfilter.excluded || [],
      assets_included: subfilter.included || [],
      panels: subfilter.panels,
      panelsExcluded: subfilter.panelsExcluded,
      polygon_geom: deserializePolygons(subfilter.polygon_geom || []),
      geographical_distribution: deserializeGeographicalDistribution(
        subfilter.geographical_distribution,
      ),
      // FIXME:
      ...deserializeAudiences(subfilter.audiences),
      isDigital: deserializeDigitalPanel(subfilter.is_digital),
      proximity: subfilter.meta_proximity,
      audiencesQuintile: deserializeAudiencesQuintile(
        subfilter.audiencesQuintile,
      ),
    }

    for (const key in deserialized) {
      removeUndefinedProperty(key, deserialized)
    }

    return deserialized
  })
}

export function deserializeLegend(legend: Plan.Legend): Plan.Legend {
  if (legend) {
    return {
      ...legend,
      poisVisibility: legend.poisVisibility !== false,
      poisBufferVisibility: legend.poisBufferVisibility !== false,
      boundariesVisibility: legend.boundariesVisibility !== false,
      assetsIdsLayerVisibility: legend.assetsIdsLayerVisibility === true,
      colorizeAssets: legend.colorizeAssets === true,
      angleNorthMap: legend.angleNorthMap === true,
      audiencesQuintile: legend.audiencesQuintile !== false,
      heatmapAssetsVisibility: legend.heatmapAssetsVisibility === true,
      heatmapAssetsType:
        legend.heatmapAssetsType ?? HEATMAP_BY_TYPES.IMPRESSIONS,
      clustersHidden: legend.clustersHidden === true,
      poisIconsVisibility: legend.poisIconsVisibility === true,
    }
  } else {
    return {
      poisVisibility: true,
      poisBufferVisibility: true,
      boundariesVisibility: true,
      assetsIdsLayerVisibility: false,
      colorizeAssets: false,
      productClass: 'l2product_id',
      canAssetsExcludedLayerBeShown: true,
      excludeAssets: false,
      facingOrientationsIcons: false,
      angleNorthMap: false,
      hiddenProductsId: [],
      audiencesQuintile: true,
      heatmapAssetsVisibility: false,
      heatmapAssetsType: HEATMAP_BY_TYPES.IMPRESSIONS,
      clustersHidden: false,
      poisIconsVisibility: false,
    }
  }
}
